// src/stores/goalForecastStore.ts
// Zustand store for Goal Forecast state management

import { create } from 'zustand';

export type GoalType = 'lose' | 'build' | 'maintain';

export interface GoalProjection {
  id: string;
  user_id: string;
  plan_id: string;
  projected_date: string;
  days_until_goal: number;
  weeks_until_goal: number;
  current_pace_kg_per_week: number | null;
  required_pace_kg_per_week: number;
  theory_pace_kg_per_week: number;
  confidence_score: number;
  status: 'ahead' | 'on_track' | 'behind' | 'stalled' | 'insufficient_data';
  theory_weight: number;
  reality_weight: number;
  plan_adherence: number | null;
  weight_logs_count: number;
  days_on_plan: number;
  calculated_at: string;
}

export interface WeightLog {
  id: string;
  weight: number;
  created_at: string;
  current_plan_id?: string | null;
}

export interface UserProfile {
  current_weight: number;
  target_weight: number;
  goal: string;
}

interface GoalForecastState {
  // Data
  projection: GoalProjection | null;
  weightHistory: WeightLog[];
  profile: UserProfile | null;
  goalType: GoalType;
  activePlanId: string | null;
  
  // Loading states
  isLoading: boolean;
  isCalculating: boolean;
  error: string | null;
  
  // Last fetch timestamp (to prevent unnecessary refetches)
  lastFetchedAt: number | null;
  
  // Actions
  setProjection: (projection: GoalProjection | null) => void;
  setWeightHistory: (history: WeightLog[]) => void;
  setProfile: (profile: UserProfile | null) => void;
  setGoalType: (goalType: GoalType) => void;
  setActivePlanId: (planId: string | null) => void;
  setLoading: (loading: boolean) => void;
  setCalculating: (calculating: boolean) => void;
  setError: (error: string | null) => void;
  setLastFetchedAt: (timestamp: number) => void;
  reset: () => void;
}

const initialState = {
  projection: null,
  weightHistory: [],
  profile: null,
  goalType: 'lose' as GoalType,
  activePlanId: null,
  isLoading: false,
  isCalculating: false,
  error: null,
  lastFetchedAt: null,
};

export const useGoalForecastStore = create<GoalForecastState>((set) => ({
  ...initialState,
  
  setProjection: (projection) => set({ projection }),
  
  setWeightHistory: (history) => set({ weightHistory: history }),
  
  setProfile: (profile) => {
    // Auto-detect goal type when profile is set
    if (profile) {
      const goal = profile.goal?.toLowerCase() || '';
      let goalType: GoalType = 'lose';
      
      if (goal.includes('build') || goal.includes('gain') || goal.includes('muscle') || goal.includes('bulk')) {
        goalType = 'build';
      } else if (goal.includes('maintain')) {
        goalType = 'maintain';
      }
      
      set({ profile, goalType });
    } else {
      set({ profile });
    }
  },
  
  setGoalType: (goalType) => set({ goalType }),
  
  setActivePlanId: (planId) => set({ activePlanId: planId }),
  
  setLoading: (loading) => set({ isLoading: loading }),
  
  setCalculating: (calculating) => set({ isCalculating: calculating }),
  
  setError: (error) => set({ error }),
  
  setLastFetchedAt: (timestamp) => set({ lastFetchedAt: timestamp }),
  
  reset: () => set(initialState),
}));